import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CssBaseline, ThemeProvider, createTheme, Box, Typography, CircularProgress } from '@mui/material';
import { NavBar } from './components/NavBar';
import { ShareList } from './components/ShareList';
import { MessageList } from './components/MessageList';
import { ShareLocations } from './components/ShareLocations';
import { CreateShare } from './components/CreateShare';
import { CloudKitConfig } from './components/CloudKitConfig';
import { DeveloperSettings } from './components/DeveloperSettings';
import { cloudKitService } from './services/cloudkit';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#f43f5e',
      light: '#fb7185',
      dark: '#e11d48',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 10px 25px rgba(0,0,0,0.08), 0 4px 10px rgba(0,0,0,0.05)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 20px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: '0 1px 3px rgba(99, 102, 241, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#475569',
            backgroundColor: '#f8fafc',
            borderBottom: '2px solid #e2e8f0',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#f1f5f9 !important',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px !important',
          '&.Mui-selected': {
            backgroundColor: '#6366f1',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#4f46e5',
            },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          gap: 4,
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid #e2e8f0 !important',
          },
        },
      },
    },
  },
});

function App() {
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    const initializeCloudKit = async () => {
      const apiToken = localStorage.getItem('cloudkit_api_token') || process.env.REACT_APP_CLOUDKIT_API_TOKEN;

      if (!apiToken) {
        setConfigError('No API token found. Please configure CloudKit below.');
        setIsLoading(false);
        return;
      }

      try {
        await cloudKitService.configure(apiToken);
        // setupAuth may hang if sign-in button elements aren't in the DOM yet,
        // so race it with a timeout and treat failure as non-fatal
        await Promise.race([
          cloudKitService.setupAuth().catch((authError) => {
            console.warn('Auth setup failed (non-fatal):', authError);
          }),
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
        setIsConfigured(true);
        setConfigError(null);
      } catch (error) {
        console.error('Failed to initialize CloudKit:', error);
        setConfigError(error instanceof Error ? error.message : 'Failed to configure CloudKit');
        setIsConfigured(false);
      }
      setIsLoading(false);
    };

    initializeCloudKit();
  }, []);

  const handleConfigured = () => {
    setIsConfigured(true);
    queryClient.invalidateQueries();
  };

  // Hidden containers for CloudKit sign-in/sign-out buttons (must exist in DOM)
  const cloudKitAuthButtons = (
    <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
      <div id="apple-sign-in-button" />
      <div id="apple-sign-out-button" />
    </div>
  );

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {cloudKitAuthButtons}
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}
        >
          <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">Loading Share It!...</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)' }}>
            {isConfigured ? (
              <>
                <NavBar />
                <Routes>
                  <Route path="/" element={<ShareList />} />
                  <Route path="/locations" element={<ShareLocations />} />
                  <Route path="/create" element={<CreateShare />} />
                  <Route path="/messages" element={<MessageList />} />
                  <Route path="/developer" element={<DeveloperSettings />} />
                </Routes>
              </>
            ) : configError ? (
              <Box p={4}>
                <Typography variant="h5" color="error" gutterBottom>
                  CloudKit Configuration Error
                </Typography>
                <Typography color="text.secondary">
                  {configError}
                </Typography>
                <CloudKitConfig onConfigured={handleConfigured} />
              </Box>
            ) : (
              <CloudKitConfig onConfigured={handleConfigured} />
            )}
          </Box>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
